import { useEffect, useMemo, useRef, useState } from 'react';
import type { UtilityListItem } from '../types/utility';
import { rankUtilities, utilityOptionLabel } from '../lib/utilityPicker';

/**
 * The "Other (not listed)" name field, which keeps searching anyway.
 *
 * Choosing "not listed" is easy to do too early — before scrolling, before
 * trying a different spelling — and a project that lands on free text loses the
 * utility's route, its download steps and its contact address for good. So the
 * field a contractor types the name into offers the same matches the picker
 * would, and taking one is exactly a picker selection: the same utility_id, the
 * same everything after. Typing something TrueLoad has never heard of still
 * stands, unchanged, which is the whole point of the escape hatch.
 */

const MIN_SEARCH = 2;
const DEBOUNCE_MS = 200;
const MAX_SUGGESTIONS = 8;

interface UtilityNameSuggestProps {
  value: string;
  projectState: string | null;
  disabled?: boolean;
  onTextChange: (text: string) => void;
  /** Taking a suggestion promotes it to a real selection. */
  onPick: (utility: UtilityListItem) => void;
  onSearch: (term: string) => Promise<UtilityListItem[]>;
}

export function UtilityNameSuggest({
  value,
  projectState,
  disabled = false,
  onTextChange,
  onPick,
  onSearch,
}: UtilityNameSuggestProps) {
  const [results, setResults] = useState<UtilityListItem[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(-1);
  // Set when a suggestion is taken or the list is dismissed, so the panel does
  // not spring back open on the re-render that follows.
  const [dismissed, setDismissed] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const runId = useRef(0);

  useEffect(() => {
    const term = value.trim();
    if (term.length < MIN_SEARCH || term === dismissed) {
      setResults([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    const id = ++runId.current;
    setSearching(true);
    const t = setTimeout(() => {
      onSearch(term)
        .then((rows) => {
          if (id !== runId.current) return;
          setResults(rows);
          setSearching(false);
          setOpen(rows.length > 0);
          setActive(-1);
        })
        .catch(() => {
          if (id !== runId.current) return;
          setResults([]);
          setSearching(false);
          setOpen(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value, dismissed, onSearch]);

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open]);

  const suggestions = useMemo(
    () => rankUtilities(results, value, projectState).slice(0, MAX_SUGGESTIONS),
    [results, value, projectState]
  );

  function take(u: UtilityListItem) {
    setDismissed(value.trim());
    setOpen(false);
    onPick(u);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => {
        const n = suggestions.length;
        if (e.key === 'ArrowDown') return i + 1 >= n ? 0 : i + 1;
        return i - 1 < 0 ? n - 1 : i - 1;
      });
      return;
    }
    if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      take(suggestions[active]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setDismissed(value.trim());
      setOpen(false);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls="utility-name-suggestions"
        aria-autocomplete="list"
        value={value}
        onChange={(e) => { setDismissed(''); onTextChange(e.target.value); }}
        onKeyDown={onKeyDown}
        onFocus={() => { if (suggestions.length > 0 && value.trim() !== dismissed) setOpen(true); }}
        placeholder="Enter utility or data provider name"
        disabled={disabled}
        data-testid="utility-other-name"
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      />

      {searching && !open && value.trim().length >= MIN_SEARCH && (
        <p className="text-xs text-gray-500 mt-1">Checking whether TrueLoad knows this utility…</p>
      )}

      {open && suggestions.length > 0 && (
        <div
          id="utility-name-suggestions"
          data-testid="utility-name-suggestions"
          className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg py-1"
        >
          <p className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
            {suggestions.length === 1
              ? 'TrueLoad already knows this utility — picking it keeps its download steps and data request'
              : 'TrueLoad already knows these — picking one keeps its download steps and data request'}
          </p>
          <ul role="listbox">
            {suggestions.map((u, i) => (
              <li
                key={u.id}
                role="option"
                aria-selected={i === active}
                data-testid={`utility-name-suggestion-${u.id}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => take(u)}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  i === active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                }`}
              >
                {utilityOptionLabel(u, projectState)}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => { setDismissed(value.trim()); setOpen(false); }}
            data-testid="utility-name-keep-typed"
            className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:text-gray-800 border-t border-gray-100"
          >
            None of these — keep "{value.trim()}" as typed
          </button>
        </div>
      )}
    </div>
  );
}
